<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4 md:p-8">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/90 backdrop-blur-sm" @click="$emit('close')"></div>

        <!-- Close Button -->
        <button 
          @click="$emit('close')"
          class="absolute top-4 right-4 z-10 p-2 text-white/50 hover:text-white transition-colors"
        >
          <XMarkIcon class="w-8 h-8" />
        </button>

        <!-- Modal Content -->
        <div class="relative w-full max-w-6xl aspect-video flex flex-col items-center justify-center">
          <!-- Main Media -->
          <div class="relative w-full h-full flex items-center justify-center">
            <Transition
              mode="out-in"
              enter-active-class="transition-all duration-300"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition-all duration-200"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div :key="currentIndex" class="w-full h-full flex items-center justify-center">
                <video 
                  v-if="currentMedia.type === 'video'" 
                  :src="currentMedia.url" 
                  controls 
                  autoplay
                  class="max-w-full max-h-full rounded-lg shadow-2xl"
                />
                <img 
                  v-else 
                  :src="currentMedia.url" 
                  :alt="currentMedia.caption || projectTitle"
                  class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  loading="eager"
                />
              </div>
            </Transition>

            <!-- Navigation Arrows -->
            <button 
              v-if="media.length > 1"
              @click="prev"
              class="absolute left-0 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white transition-colors"
            >
              <ChevronLeftIcon class="w-12 h-12" />
            </button>
            <button 
              v-if="media.length > 1"
              @click="next"
              class="absolute right-0 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white transition-colors"
            >
              <ChevronRightIcon class="w-12 h-12" />
            </button>
          </div>

          <!-- Caption & Info -->
          <div class="mt-6 text-center space-y-2">
            <p v-if="currentMedia.caption" class="text-white text-lg font-medium">
              {{ currentMedia.caption }}
            </p>
            <p class="text-white/50 text-sm">
              {{ currentIndex + 1 }} / {{ media.length }} — {{ projectTitle }}
            </p>
          </div>

          <!-- Thumbnails -->
          <div v-if="media.length > 1" class="mt-8 flex items-center justify-center space-x-2 overflow-x-auto max-w-full p-2">
            <button
              v-for="(item, index) in media"
              :key="index"
              @click="currentIndex = index"
              class="relative w-16 h-12 rounded overflow-hidden flex-shrink-0 transition-all duration-300 border-2"
              :style="{ 
                borderColor: currentIndex === index ? accentColor : 'transparent',
                opacity: currentIndex === index ? '1' : '0.5'
              }"
            >
              <video v-if="item.type === 'video'" :src="item.url" class="w-full h-full object-cover" />
              <img v-else :src="item.url" class="w-full h-full object-cover" loading="lazy" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'

interface MediaItem {
  url: string
  type: 'image' | 'video'
  caption?: string
  order: number
}

interface Props {
  isOpen: boolean
  media: MediaItem[]
  projectTitle: string
  accentColor: string
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])

const currentIndex = ref(0)
const currentMedia = computed(() => props.media[currentIndex.value])

const next = () => {
  currentIndex.value = (currentIndex.value + 1) % props.media.length
}

const prev = () => {
  currentIndex.value = currentIndex.value === 0 
    ? props.media.length - 1 
    : currentIndex.value - 1
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'ArrowRight') next()
  if (e.key === 'ArrowLeft') prev()
  if (e.key === 'Escape') props.isOpen && emit('close')
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.aspect-video {
  aspect-ratio: 16 / 9;
}
</style>

